// src/pages/CommunityPage.js
// Two feeds: Notices (short-lived local announcements — lost dog, road
// closure, club news) and Meet Locals (activity-first — "join me for a
// walk", never framed around loneliness). Public browsing, no login
// required; posting requires sign-in. Meet Locals auto-expires after 30
// days server-side (expireMeetLocals scheduled function).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getNotices, getMeetLocals, createNotice, createMeetLocal, deleteNotice, deleteMeetLocal } from '../lib/db';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import BottomNav from '../components/layout/BottomNav';
import { HeaderAvatar } from '../components/shared/HeaderAvatar';
import { SponsorStrip } from '../components/shared/SponsorStrip';
import { PageBack } from '../components/shared/PageBack';
import { ReferBanner } from '../components/shared/ReferBanner';

export const NOTICE_CATEGORIES = [
  { value: 'general',  label: 'General',       emoji: '📌' },
  { value: 'lost_found',label: 'Lost & Found',  emoji: '🐾' },
  { value: 'roadworks', label: 'Roadworks',     emoji: '🚧' },
  { value: 'club_news', label: 'Club News',     emoji: '📣' },
  { value: 'free_stuff',label: 'Free Stuff',    emoji: '🎁' },
];

export const ACTIVITY_TYPES = [
  { value: 'walk',    label: 'A walk',           emoji: '🚶' },
  { value: 'coffee',  label: 'Coffee or a chat',  emoji: '☕' },
  { value: 'hobby',   label: 'A hobby or activity', emoji: '🎨' },
  { value: 'sport',   label: 'Sport or fitness',  emoji: '⚽' },
  { value: 'other',   label: 'Something else',    emoji: '💬' },
];

const CommunityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('notices'); // 'notices' | 'meetlocals'
  const [notices, setNotices] = useState([]);
  const [meetLocals, setMeetLocals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Post form state
  const [postTitle, setPostTitle] = useState('');
  const [postDescription, setPostDescription] = useState('');
  const [postCategory, setPostCategory] = useState(NOTICE_CATEGORIES[0].value);
  const [postPhone, setPostPhone] = useState('');
  const [postEmail, setPostEmail] = useState('');
  const [postContactPrefs, setPostContactPrefs] = useState(['call', 'text', 'email']);
  const [posting, setPosting] = useState(false);

  const townId = localStorage.getItem('gather-town-id') || 'loughrea';

  useEffect(() => {
    setLoading(true);
    Promise.all([getNotices(townId), getMeetLocals(townId)])
      .then(([n, m]) => { setNotices(n); setMeetLocals(m); })
      .catch(err => { console.error(err); toast.error('Could not load Community'); })
      .finally(() => setLoading(false));
  }, [townId]);

  const openPost = () => {
    if (!user) { navigate('/login'); return; }
    setPostTitle('');
    setPostDescription('');
    setPostCategory(tab === 'notices' ? NOTICE_CATEGORIES[0].value : ACTIVITY_TYPES[0].value);
    setPostPhone('');
    setPostEmail('');
    setPostContactPrefs(['call', 'text', 'email']);
    setShowPost(true);
  };

  const handlePost = async () => {
    if (!postTitle.trim() || !postDescription.trim()) {
      toast.error('Please add a title and description');
      return;
    }
    setPosting(true);
    try {
      if (tab === 'notices') {
        const ref = await createNotice({
          title: postTitle.trim(),
          description: postDescription.trim(),
          category: postCategory,
          townId,
          postedBy: user.uid,
          phone: postPhone.trim() || null,
          email: postEmail.trim() || null,
          contactPrefs: postContactPrefs,
        });
        setNotices(n => [{ id: ref.id, title: postTitle.trim(), description: postDescription.trim(), category: postCategory, townId, postedBy: user.uid, phone: postPhone.trim() || null, email: postEmail.trim() || null, contactPrefs: postContactPrefs, status: 'live', createdAt: new Date() }, ...n]);
      } else {
        const ref = await createMeetLocal({
          title: postTitle.trim(),
          description: postDescription.trim(),
          activityType: postCategory,
          townId,
          postedBy: user.uid,
          phone: postPhone.trim() || null,
          email: postEmail.trim() || null,
          contactPrefs: postContactPrefs,
        });
        setMeetLocals(m => [{ id: ref.id, title: postTitle.trim(), description: postDescription.trim(), activityType: postCategory, townId, postedBy: user.uid, phone: postPhone.trim() || null, email: postEmail.trim() || null, contactPrefs: postContactPrefs, status: 'live', createdAt: new Date() }, ...m]);
      }
      toast.success('Posted!');
      setShowPost(false);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setPosting(false);
  };

  const handleDelete = async (id, isNotice) => {
    try {
      if (isNotice) {
        await deleteNotice(id);
        setNotices(n => n.filter(x => x.id !== id));
      } else {
        await deleteMeetLocal(id);
        setMeetLocals(m => m.filter(x => x.id !== id));
      }
      toast.success('Removed');
    } catch (err) {
      console.error(err);
      toast.error('Could not remove — please try again');
    }
  };

  const fmtDate = (d) => {
    const date = d?.toDate ? d.toDate() : new Date(d);
    return format(date, 'd MMM');
  };

  const list = tab === 'notices' ? notices : meetLocals;
  const categoryOptions = tab === 'notices' ? NOTICE_CATEGORIES : ACTIVITY_TYPES;
  const filteredList = list.filter(item => {
    const matchesCategory = categoryFilter === 'all' || (item.category || item.activityType) === categoryFilter;
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || item.title?.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="page" style={{ background: 'var(--cream)' }}>
      <div style={{ padding: '52px 20px 0' }}><PageBack fallbackPath="/" /></div>

      <div style={{ padding: '0 20px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-0.3px', margin: 0 }}>Community</h1>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 3 }}>What's happening, and who's around</div>
          </div>
          <HeaderAvatar />
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        <SponsorStrip townId={townId} page="community" />
      </div>

      {/* Tab toggle */}
      <div style={{ display: 'flex', gap: 8, padding: '0 20px 14px' }}>
        <button
          onClick={() => setTab('notices')}
          style={{
            flex: 1, padding: '12px 14px', borderRadius: 14, border: 'none',
            background: tab === 'notices' ? 'var(--sage-dark)' : 'var(--white)',
            color: tab === 'notices' ? 'white' : 'var(--ink)',
            fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
          }}
        >
          📌 Noticeboard
        </button>
        <button
          onClick={() => setTab('meetlocals')}
          style={{
            flex: 1, padding: '12px 14px', borderRadius: 14, border: 'none',
            background: tab === 'meetlocals' ? 'var(--sage-dark)' : 'var(--white)',
            color: tab === 'meetlocals' ? 'white' : 'var(--ink)',
            fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
          }}
        >
          🤝 Local Meet-ups
        </button>
      </div>

      {/* Post button */}
      <div style={{ padding: '0 20px 14px' }}>
        <button onClick={openPost} style={{
          width: '100%', padding: '13px 16px', borderRadius: 14,
          background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none',
          fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
        }}>
          {tab === 'notices' ? '+ Post to the Noticeboard' : '+ Suggest something to do together'}
        </button>
      </div>

      {/* Post form */}
      {showPost && (
        <div style={{ margin: '0 20px 16px', background: 'var(--white)', borderRadius: 16, padding: 16, border: '1.5px solid var(--border)' }}>
          <input
            className="form-input"
            aria-label={tab === 'notices' ? "What's the notice about?" : "What would you like to do?"}
            placeholder={tab === 'notices' ? "What's the notice about?" : "What would you like to do?"}
            value={postTitle}
            onChange={e => setPostTitle(e.target.value)}
            style={{ marginBottom: 10 }}
          />
          <textarea
            className="form-input"
            aria-label="A few more details"
            placeholder="A few more details..."
            value={postDescription}
            onChange={e => setPostDescription(e.target.value)}
            rows={3}
            style={{ marginBottom: 10, resize: 'vertical' }}
          />
          <select
            className="form-input"
            aria-label="Category"
            value={postCategory}
            onChange={e => setPostCategory(e.target.value)}
            style={{ marginBottom: 10 }}
          >
            {categoryOptions.map(c => (
              <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, marginBottom: 6 }}>
            Optional — so people can actually get in touch with you
          </div>
          <input
            className="form-input"
            type="tel"
            aria-label="Phone (optional)"
            placeholder="Phone (optional)"
            value={postPhone}
            onChange={e => setPostPhone(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <input
            className="form-input"
            type="email"
            aria-label="Email (optional)"
            placeholder="Email (optional)"
            value={postEmail}
            onChange={e => setPostEmail(e.target.value)}
            style={{ marginBottom: 10 }}
          />

          {(postPhone.trim() || postEmail.trim()) && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, marginBottom: 6 }}>
                How would you like people to contact you?
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                All three are switched on by default — tap any one to turn it off.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  ['call', '📞 Call'],
                  ['text', '💬 Text'],
                  ['email', '✉️ Email'],
                ].map(([key, label]) => {
                  const checked = postContactPrefs.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPostContactPrefs(checked ? postContactPrefs.filter(p => p !== key) : [...postContactPrefs, key])}
                      style={{
                        flex: 1, padding: '9px 8px', borderRadius: 10, border: checked ? '2px solid var(--sage-deep)' : '2px solid transparent', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, fontFamily: 'Nunito',
                        background: checked ? 'var(--sage-deep)' : '#F0F0EE',
                        color: checked ? 'white' : 'var(--muted)',
                      }}
                    >
                      {checked ? '✓ ' : ''}{label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowPost(false)} style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 12, padding: '11px 12px', fontSize: 13.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handlePost} disabled={posting} style={{ flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '11px 12px', fontSize: 13.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: posting ? 0.7 : 1 }}>
              {posting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      )}

      {/* Search + filter */}
      <div style={{ padding: '0 20px 12px', display: 'flex', gap: 8 }}>
        <input
          className="form-input"
          aria-label={`Search ${tab === 'notices' ? 'Noticeboard' : 'Meet-ups'}`}
          placeholder={`Search ${tab === 'notices' ? 'the Noticeboard' : 'meet-ups'}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <select
          className="form-input"
          aria-label="Filter by category"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{ width: 130, flexShrink: 0 }}
        >
          <option value="all">All</option>
          {categoryOptions.map(c => (
            <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </div>

      {/* Feed */}
      <div style={{ padding: '0 18px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Loading...</div>
        ) : list.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">{tab === 'notices' ? '📌' : '🤝'}</div>
            <div className="empty-state-title">
              {tab === 'notices' ? 'Nothing on the Noticeboard yet' : 'No plans posted yet'}
            </div>
            <div className="empty-state-body">
              {tab === 'notices'
                ? 'Be the first to share something with the town.'
                : 'Suggest a walk, a coffee, or anything else — see who joins.'}
            </div>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <div className="empty-state-title">No matches</div>
            <div className="empty-state-body">Try a different search or category.</div>
          </div>
        ) : filteredList.map(item => {
          const catInfo = categoryOptions.find(c => c.value === (item.category || item.activityType)) || categoryOptions[0];
          const isMine = user && item.postedBy === user.uid;
          const preview = item.description && item.description.length > 90 ? item.description.slice(0, 90).trim() + '…' : item.description;
          return (
            <button
              key={item.id}
              onClick={() => navigate(`/${tab === 'notices' ? 'notice' : 'meetup'}/${item.id}`)}
              style={{ width: '100%', textAlign: 'left', background: 'var(--white)', borderRadius: 16, padding: 16, border: '1.5px solid var(--border)', cursor: 'pointer', display: 'block' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ display: 'inline-block', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontSize: 11, fontWeight: 700, borderRadius: 100, padding: '4px 10px', marginBottom: 8 }}>
                  {catInfo.emoji} {catInfo.label}
                </span>
                {isMine && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id, tab === 'notices'); }}
                    style={{ color: 'var(--muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >
                    Remove
                  </span>
                )}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>{item.title}</div>
              {preview && <div style={{ fontSize: 13.5, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.5, marginBottom: 6 }}>{preview}</div>}
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{fmtDate(item.createdAt)}</div>
            </button>
          );
        })}
      </div>

      <PageBack position="bottom" fallbackPath="/" />
      <ReferBanner />
      <BottomNav />
    </div>
  );
};

export default CommunityPage;
